export type GoogleMapPoint = { lat: number; lng: number };
export type GoogleMapBounds = { extend(point: GoogleMapPoint): void };
export type GoogleMapListener = { remove(): void };
export type GoogleMapLatLng = { lat(): number; lng(): number };
export type GoogleMapMouseEvent = { latLng: GoogleMapLatLng | null };

export type GoogleMapInstance = {
  addListener(
    eventName: 'click',
    listener: (event: GoogleMapMouseEvent) => void
  ): GoogleMapListener;
  setCenter(point: GoogleMapPoint): void;
  setZoom(zoom: number): void;
  fitBounds(bounds: GoogleMapBounds, padding?: number): void;
  setOptions(options: { draggableCursor: string | null }): void;
};

export type GoogleMapOverlay = { setMap(map: GoogleMapInstance | null): void };

// Círculo e marcador podem ser arrastados; os eventos devolvem a posição nova.
export type GoogleMapCircle = GoogleMapOverlay & {
  setCenter(point: GoogleMapPoint): void;
  getCenter(): GoogleMapLatLng;
  setDraggable(draggable: boolean): void;
  addListener(
    eventName: 'click' | 'drag' | 'dragend',
    listener: (event: GoogleMapMouseEvent) => void
  ): GoogleMapListener;
};

export type GoogleMapMarker = GoogleMapOverlay & {
  setPosition(point: GoogleMapPoint): void;
  getPosition(): GoogleMapLatLng;
  setDraggable(draggable: boolean): void;
  addListener(
    eventName: 'drag' | 'dragend',
    listener: (event: GoogleMapMouseEvent) => void
  ): GoogleMapListener;
};

export type GoogleMapPolyline = GoogleMapOverlay & {
  setPath(path: GoogleMapPoint[]): void;
};

type OverlayStyle = {
  map: GoogleMapInstance;
  strokeColor: string;
  strokeOpacity: number;
  strokeWeight: number;
  fillColor: string;
  fillOpacity: number;
  clickable: boolean;
};

export type GoogleMapsApi = {
  Map: new (
    element: HTMLElement,
    options: {
      center: GoogleMapPoint;
      zoom: number;
      mapTypeControl: boolean;
      streetViewControl: boolean;
      fullscreenControl: boolean;
      clickableIcons: boolean;
      gestureHandling: 'greedy';
    }
  ) => GoogleMapInstance;
  Circle: new (
    options: OverlayStyle & {
      center: GoogleMapPoint;
      radius: number;
      draggable?: boolean;
    }
  ) => GoogleMapCircle;
  Marker: new (options: {
    map: GoogleMapInstance;
    position: GoogleMapPoint;
    title?: string;
    clickable?: boolean;
    draggable?: boolean;
    label?: {
      text: string;
      color: string;
      fontSize: string;
      fontWeight: string;
    };
    // `path`: um símbolo pronto (`SymbolPath`) ou um caminho SVG.
    icon?: {
      path: number | string;
      scale: number;
      fillColor: string;
      fillOpacity: number;
      strokeColor: string;
      strokeWeight: number;
    };
  }) => GoogleMapMarker;
  Polyline: new (options: {
    map: GoogleMapInstance;
    path: GoogleMapPoint[];
    strokeOpacity: number;
    clickable: boolean;
    icons: {
      icon: {
        path: string;
        strokeColor: string;
        strokeOpacity: number;
        scale: number;
      };
      offset: string;
      repeat: string;
    }[];
  }) => GoogleMapPolyline;
  SymbolPath: { CIRCLE: number };
  // Busca de endereços (Geocoding API); depende de ela estar liberada para a chave.
  Geocoder: new () => {
    geocode(request: { address: string; region: string }): Promise<{
      results: {
        geometry: {
          location: { lat(): number; lng(): number };
          viewport?: GoogleMapBounds;
        };
      }[];
    }>;
  };
  LatLngBounds: new () => GoogleMapBounds;
};

declare global {
  interface Window {
    google?: { maps?: GoogleMapsApi };
    __webcolGoogleMapsReady?: () => void;
  }
}

let configuredKey: string | null = null;
let mapsApiPromise: Promise<GoogleMapsApi> | null = null;

export function loadGoogleMaps(apiKey: string): Promise<GoogleMapsApi> {
  const normalizedKey = apiKey.trim();
  if (!normalizedKey) {
    return Promise.reject(
      new Error(
        'Configure a chave do Google Maps (REACT_APP_GOOGLE_MAPS_API_KEY) para carregar o mapa.'
      )
    );
  }

  if (configuredKey && configuredKey !== normalizedKey) {
    return Promise.reject(
      new Error('O Google Maps já foi iniciado com outra chave.')
    );
  }

  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (mapsApiPromise) return mapsApiPromise;

  configuredKey = normalizedKey;
  mapsApiPromise = new Promise<GoogleMapsApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.dataset.webcolGoogleMaps = 'true';
    script.onerror = () => {
      mapsApiPromise = null;
      reject(new Error('Não foi possível carregar o Google Maps.'));
    };
    window.__webcolGoogleMapsReady = () => {
      const maps = window.google?.maps;
      if (!maps) {
        mapsApiPromise = null;
        reject(new Error('O Google Maps não foi iniciado corretamente.'));
        return;
      }
      resolve(maps);
    };
    script.src =
      'https://maps.googleapis.com/maps/api/js' +
      `?key=${encodeURIComponent(normalizedKey)}` +
      '&callback=__webcolGoogleMapsReady&v=weekly&language=pt-BR&region=BR';
    document.head.appendChild(script);
  });

  return mapsApiPromise;
}
